import { useEffect, useState, useCallback } from "react";
import { Star, Trash2, Check, EyeOff, MessageSquare, ShieldCheck, RefreshCw, Reply, Upload, Download, Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  adminListReviews, adminUpdateReview, adminDeleteReview,
  adminImportReviewsBulk, adminExportReviews,
  adminGetReviewSettings, adminUpdateReviewSettings, adminApproveAllPendingReviews,
  listReadyMade,
} from "../../lib/api";

/**
 * Admin → Reviews moderation queue.
 *
 * Tabs: Pending / Published / Hidden / All.
 * - Approve (publish), Hide, or Delete each review.
 * - Inline admin-reply textbox per row so the operator can respond to
 *   negative reviews without leaving the table.
 */
const STATUS_TABS = [
  { k: "pending", l: "Pending", cls: "text-amber-400" },
  { k: "published", l: "Published", cls: "text-emerald-400" },
  { k: "hidden", l: "Hidden", cls: "text-zinc-500" },
  { k: "all", l: "All", cls: "text-zinc-300" },
];

function Stars({ rating }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={12}
          className={n <= rating ? "text-yellow-400 fill-yellow-400" : "text-zinc-700"}
        />
      ))}
    </span>
  );
}

export default function ReviewsTab({ pw }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDraft, setReplyDraft] = useState({});
  // Moderation config — read from /api/admin/review-settings on mount so
  // the admin sees the global "auto-publish every review" toggle plus
  // the count of currently-pending reviews waiting for their attention.
  const [autoApprove, setAutoApprove] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  // Counts per status bucket so the tabs show how many sit in each.
  // Without these the operator hits "Pending: 0" and assumes no reviews
  // exist, when actually they're all sitting under Published. Reported
  // by the shop owner Feb 2026.
  const [bucketCounts, setBucketCounts] = useState({ pending: 0, published: 0, hidden: 0, all: 0 });
  // Tracks whether we've already done the one-shot "auto-jump to a
  // non-empty tab if the default Pending is empty" on first load.
  const initialJumpDoneRef = useState({ done: false })[0];
  // Ready-Made listing lookup so each review row can show the linked
  // product name + a "View listing" link to /design/{id} (iter47f).
  const [productMap, setProductMap] = useState({});

  useEffect(() => {
    // Load ready-made listings once on mount so we can resolve product_id
    // → { name, slug } for the per-review "View listing" link.
    listReadyMade()
      .then((arr) => {
        const m = {};
        for (const p of arr || []) m[p.id] = p;
        setProductMap(m);
      })
      .catch(() => {});
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const s = await adminGetReviewSettings(pw);
      setAutoApprove(!!s.auto_approve_reviews);
      setPendingCount(s.pending_count || 0);
    } catch {
      /* non-fatal */
    }
  }, [pw]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // Load the visible bucket AND the full list (for accurate counts).
      const [bucket, all] = await Promise.all([
        adminListReviews(pw, status === "all" ? undefined : status),
        adminListReviews(pw),  // no status filter → everything
      ]);
      setRows(bucket || []);
      const counts = { pending: 0, published: 0, hidden: 0, all: (all || []).length };
      for (const r of all || []) {
        const k = r.status;
        if (k in counts) counts[k] += 1;
      }
      setBucketCounts(counts);
      await loadSettings();

      // First-load convenience: if the operator landed on Pending but
      // there's nothing pending AND there are published reviews, jump
      // them straight to the Published bucket so they don't think the
      // app lost their data. Only fires once per mount.
      if (!initialJumpDoneRef.done) {
        initialJumpDoneRef.done = true;
        if (status === "pending" && counts.pending === 0 && counts.published > 0) {
          setStatus("published");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [pw, status, loadSettings, initialJumpDoneRef]);

  useEffect(() => { reload(); }, [reload]);

  const toggleAutoApprove = async (val) => {
    try {
      const res = await adminUpdateReviewSettings({ auto_approve_reviews: val }, pw);
      setAutoApprove(res.auto_approve_reviews);
      toast.success(val
        ? "Auto-approve ON — new reviews publish instantly"
        : "Auto-approve OFF — new reviews go to the pending queue");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    }
  };

  const approveAllPending = async () => {
    if (!window.confirm(`Approve all ${pendingCount} pending review(s)? They'll be published immediately.`)) return;
    try {
      const res = await adminApproveAllPendingReviews(pw);
      toast.success(`Approved ${res.approved} review(s)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approve failed");
    }
  };

  const setStatusOn = async (rid, newStatus) => {
    try {
      await adminUpdateReview(rid, { status: newStatus }, pw);
      toast.success(`Review ${newStatus}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveReply = async (rid) => {
    try {
      await adminUpdateReview(rid, { admin_reply: replyDraft[rid] || "" }, pw);
      toast.success("Reply saved");
      setReplyDraft((d) => ({ ...d, [rid]: undefined }));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply save failed");
    }
  };

  const remove = async (rid) => {
    if (!window.confirm("Hard-delete this review? This cannot be undone.")) return;
    try {
      await adminDeleteReview(rid, pw);
      toast.success("Review deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const reviews = Array.isArray(data) ? data : (data.reviews || []);
      if (!Array.isArray(reviews) || !reviews.length) {
        toast.error("File must be a JSON array of reviews (or {reviews:[...]})");
        return;
      }
      if (!window.confirm(`Import ${reviews.length} reviews? Existing rows (by id) will be skipped.`)) return;
      const res = await adminImportReviewsBulk(reviews, pw);
      toast.success(`Imported ${res.inserted} new · skipped ${res.skipped_existing} existing`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Import failed");
    }
  };

  return (
    <div data-testid="reviews-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl uppercase flex items-center gap-3">
            <MessageSquare size={22} className="text-[var(--tc-red)]" /> Reviews
          </h2>
          <p className="font-mono text-xs text-zinc-500 mt-1">
            Verified purchases auto-publish. Guest reviews land in <strong>Pending</strong>.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={async () => { try { await adminExportReviews(pw); toast.success("Downloaded"); } catch { toast.error("Export failed"); } }}
            data-testid="reviews-export-btn"
            className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10"
            title="Download all reviews as JSON — re-import on production after deploy"
          >
            <Download size={14} /> Export JSON
          </button>
          <label
            data-testid="reviews-import-btn"
            className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10 cursor-pointer"
            title="Bulk import reviews from a JSON file (e.g. Etsy export). Skips rows that already exist by id."
          >
            <Upload size={14} /> Import JSON
            <input type="file" accept=".json,application/json" onChange={importFile} className="sr-only" data-testid="reviews-import-input" />
          </label>
          <button onClick={reload} data-testid="reviews-refresh" className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="surface-card p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="review-moderation-banner">
        <div className="flex items-start gap-3">
          <Zap size={18} className={autoApprove ? "text-emerald-400" : "text-amber-400"} />
          <div>
            <div className="font-ui text-sm font-bold uppercase tracking-wider">
              Auto-approve reviews: {autoApprove ? "ON" : "OFF"}
            </div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">
              {autoApprove
                ? "All new reviews go live instantly. Flip OFF to require manual approval."
                : "New reviews wait in the Pending queue below. Flip ON to skip moderation."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <button
              onClick={approveAllPending}
              data-testid="approve-all-pending-btn"
              className="btn-tc-primary inline-flex items-center gap-2 text-xs h-10"
              title="Publish every currently-pending review in one click"
            >
              <Check size={14} /> Approve all {pendingCount} pending
            </button>
          )}
          <button
            onClick={() => toggleAutoApprove(!autoApprove)}
            data-testid="toggle-auto-approve"
            className={`inline-flex items-center gap-2 text-xs h-10 px-4 border font-mono tracking-widest ${
              autoApprove
                ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            }`}
          >
            {autoApprove ? "TURN OFF" : "TURN ON"}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="review-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`review-status-${t.k}`}
            onClick={() => setStatus(t.k)}
            className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest uppercase flex items-center gap-2 ${
              status === t.k ? "bg-[var(--tc-red)] border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400 hover:text-white"
            }`}
          >
            {t.l}
            <span
              className={`px-1.5 py-0.5 border text-[9px] ${
                status === t.k ? "border-white/40 text-white" : "border-[#333] text-zinc-500"
              }`}
              data-testid={`review-count-${t.k}`}
            >
              {bucketCounts[t.k] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <MessageSquare size={32} className="mx-auto mb-3 text-zinc-700" />
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
            No reviews in this bucket.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="surface-card p-5" data-testid={`review-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars rating={r.rating} />
                    <span className="font-ui text-sm font-bold">{r.customer_name}</span>
                    {r.verified_purchase && (
                      <span className="font-mono text-[9px] tracking-widest text-emerald-400 inline-flex items-center gap-1">
                        <ShieldCheck size={10} /> VERIFIED
                      </span>
                    )}
                    <span className={`font-mono text-[10px] tracking-widest uppercase ${
                      r.status === "published" ? "text-emerald-400"
                      : r.status === "hidden" ? "text-zinc-500" : "text-amber-400"
                    }`}>
                      · {r.status}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {r.customer_email && <span>{r.customer_email}</span>}
                    {/* Linked product — clickable when we can resolve a real
                        Ready-Made listing or when a manual source_url is set.
                        Shows the human-readable product name instead of the
                        opaque `rm-xxxxxx` id (iter47f). */}
                    {(() => {
                      const product = r.product_type === "ready_made" ? productMap[r.product_id] : null;
                      let href = r.source_url || null;
                      let label = r.product_id;
                      if (product) {
                        href = `/design/${product.id}`;
                        label = product.name;
                      } else if (r.product_type === "ready_made") {
                        href = `/design/${r.product_id}`;
                      }
                      if (href) {
                        return (
                          <a
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`review-listing-link-${r.id}`}
                            className="inline-flex items-center gap-1 text-[var(--tc-red)] hover:underline max-w-[28ch] truncate"
                            title={label}
                          >
                            <ExternalLink size={11} />
                            <span className="truncate">{label}</span>
                          </a>
                        );
                      }
                      return <span>{label} ({r.product_type})</span>;
                    })()}
                    {r.order_number && <span>· Order {r.order_number}</span>}
                    <span>· {new Date(r.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {r.status !== "published" && (
                    <button
                      data-testid={`review-publish-${r.id}`}
                      onClick={() => setStatusOn(r.id, "published")}
                      className="border border-emerald-700 hover:bg-emerald-950 text-emerald-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1"
                    >
                      <Check size={12} /> APPROVE
                    </button>
                  )}
                  {r.status !== "hidden" && (
                    <button
                      data-testid={`review-hide-${r.id}`}
                      onClick={() => setStatusOn(r.id, "hidden")}
                      className="border border-[#333] hover:bg-[#111] text-zinc-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1"
                    >
                      <EyeOff size={12} /> HIDE
                    </button>
                  )}
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="border border-red-900 hover:bg-red-950 text-red-300 px-3 py-1.5 font-mono text-[10px] tracking-widest flex items-center gap-1"
                  >
                    <Trash2 size={12} /> DELETE
                  </button>
                </div>
              </div>
              {r.title && <h4 className="font-display text-lg uppercase mb-1">{r.title}</h4>}
              <p className="text-sm text-zinc-300 whitespace-pre-wrap mb-2">{r.body || <em className="text-zinc-600">(no body)</em>}</p>
              {(r.photos || []).length > 0 && (
                <div className="flex gap-2 flex-wrap mb-3">
                  {r.photos.map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer">
                      <img src={src} alt={`review-photo-${i}`} className="w-20 h-20 object-cover border border-[#222]" />
                    </a>
                  ))}
                </div>
              )}

              {/* Admin reply */}
              <div className="border-t border-[#222] mt-3 pt-3">
                {r.admin_reply && replyDraft[r.id] === undefined ? (
                  <div className="bg-[#0a0a0a] border-l-2 border-[var(--tc-red)] p-3">
                    <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">RESPONSE FROM TORONTO CUSTOM</div>
                    <div className="text-sm text-zinc-200 whitespace-pre-wrap">{r.admin_reply}</div>
                    <button
                      onClick={() => setReplyDraft((d) => ({ ...d, [r.id]: r.admin_reply }))}
                      className="font-mono text-[10px] text-zinc-500 hover:text-white mt-2"
                    >
                      EDIT REPLY
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <textarea
                      rows={2}
                      value={replyDraft[r.id] ?? ""}
                      onChange={(e) => setReplyDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                      placeholder="Write a public reply (visible under the review)…"
                      data-testid={`review-reply-input-${r.id}`}
                      className="flex-1 bg-black border border-[#222] px-3 py-2 font-body text-sm focus:border-[var(--tc-red)] outline-none"
                    />
                    <button
                      data-testid={`review-reply-save-${r.id}`}
                      onClick={() => saveReply(r.id)}
                      className="btn-tc-primary text-xs px-4 flex items-center gap-1 self-stretch"
                    >
                      <Reply size={12} /> SAVE
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
